import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface CountryCount {
  code: string;
  label: string;
  count: number;
}

export interface TypeCount {
  key: string;
  label: string;
  count: number;
}

export interface JobBoardStats {
  remoteCount: number;
  countries: CountryCount[];
  types: TypeCount[];
  totalCount: number;
  remoteFinanceCount: number;
  remoteOtherRemoteCount: number;
  recentJobsCount: number;
  leadershipCount: number;
}

// All stats come from the job_board_stats snapshot (refreshed every 30 min by
// pg_cron — see supabase/migrations/20260720193000_job_board_stats_snapshot.sql).
// This used to be ~15 count+ilike queries per page view averaging 0.4–1.3 s each.
export const useJobBoardStats = () => {
  return useQuery<JobBoardStats>({
    queryKey: ["jobBoardStats"],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await (supabase.rpc as any)("get_job_board_stats");
      if (error) throw error;

      const row = (Array.isArray(data) ? data[0] : data) as any;
      const countries = (((row?.countries ?? []) as CountryCount[]).slice()).sort(
        (a, b) => b.count - a.count
      );
      const types = (((row?.types ?? []) as TypeCount[]).slice()).sort(
        (a, b) => b.count - a.count
      );
      const remoteCount = row?.remote_count ?? 0;
      const remoteFinanceCount = row?.remote_finance_count ?? 0;

      return {
        remoteCount,
        countries,
        types,
        totalCount: row?.total_count ?? 0,
        remoteFinanceCount,
        remoteOtherRemoteCount: Math.max(0, remoteCount - remoteFinanceCount),
        recentJobsCount: row?.recent_jobs_count ?? 0,
        leadershipCount: row?.leadership_count ?? 0,
      } satisfies JobBoardStats;
    },
  });
};
